import { inject, Injectable } from '@angular/core';
import { BrowserService } from '@core/services/browser/browser.service';
import { UserService } from '@core/services';
import { SessionService, StorageService } from '@movilidad/app-core';
import {
  AlertController,
  IonicSafeString,
  ModalController,
  NavController,
  ToastController,
} from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { User } from '@shared/models';
import { isCampusScopedDetailKey } from '@shared/utils';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { RegisterSamlModalComponent } from '../login/components/register-saml-modal/register-saml-modal.component';
import { ApiService } from './api.service';
import { LoadingService } from './loading.service';
import { MovilidadSessionHooks } from './session-hooks';
import { getRedirectPath, needsBirthdayCapture } from './session.config';

/**
 * Fachada de Movilidad sobre la sesión (TEC-293). El token, `me`, `user$` y el logout viven en
 * `SessionService` de @movilidad/app-core; aquí queda lo propio del app: los flujos de login
 * (correo, PIN de conductor, registro, SSO del Tec con su modal), la redirección por tipo de usuario
 * y las consultas que las páginas le piden. La API pública no cambió.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storage = inject(StorageService);
  private api = inject(ApiService);
  private toastCtrl = inject(ToastController);
  private navCtrl = inject(NavController);
  private alertCtrl = inject(AlertController);
  private loadingService = inject(LoadingService);
  private userService = inject(UserService);
  private modalCtrl = inject(ModalController);
  #session = inject(SessionService);
  #browser = inject(BrowserService);
  #queryClient = inject(QueryClient);
  #campusResync: Promise<void> | null = null;
  #campusSwitch: Promise<unknown> | null = null;

  constructor() {
    // Los hooks del logout (y con ellos DriverStore y PushRegistrationService) se siguen creando
    // junto con AuthService al arrancar, como cuando el logout vivía aquí: DriverStore se suscribe a
    // `user$` desde el arranque, y SessionService sólo resuelve los hooks al cerrar sesión.
    inject(MovilidadSessionHooks);
  }

  async getUser() {
    return (await this.#session.getStoredUser<User>()) || null;
  }

  needsBirthdayCapture(user: User | null): boolean {
    return needsBirthdayCapture(user);
  }

  getUserFromServer() {
    return (
      this.api
        .getData('user')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then(async (res: any) => {
          if (res && res.success && res.user) {
            // TEC-380: una sola escritura de `me` (la de updateUser) y, cuando el modal de términos se
            // abre, `user$` ya trae a este usuario.
            await this.userService.updateUser(res.user);
            this.#checkTermsAndConditions(res.user);
            return res.user;
          }
          return null;
        })
    );
  }

  checkRedirect() {
    this.#session.getStoredUser().then((user) => {
      if (user) {
        this.redirectTo(user);
      } else {
        this.logout();
      }
    });
  }

  getRedirectPath(user: User): string {
    return getRedirectPath(user);
  }

  redirectTo(user) {
    if (!user) {
      this.logout();
      return;
    }
    this.navCtrl.navigateRoot([this.getRedirectPath(user)]);
  }

  async checkRedirectFromServer() {
    try {
      const storedUser = await this.getUser();
      if (storedUser) {
        const serverUser = await this.getUserFromServer();
        if (serverUser) {
          Sentry.setUser({ id: String(serverUser.id) }); // sin correo: PII (auditoría 2026-10)
          this.redirectTo(serverUser);
        }
      }
    } catch (error) {
      console.error('checkRedirectFromServer error:', error);
    }
  }

  async presentToast(msg = '') {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 3000,
      position: 'top',
    });
    return await toast.present();
  }

  switchCampus(campusId) {
    this.loadingService.present();
    const switching = this.api
      .putData('profile/campus_current/' + campusId, {})
      .then(async (res) => {
        if (res && res.success && res.user) {
          await this.applyCampusChange(res.user);
          return res.user;
        }
        return null;
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
    this.#campusSwitch = switching.catch(() => null);
    return switching;
  }

  /**
   * Lo que pasa en el app cuando el campus del usuario cambió: aquí mismo (`switchCampus`) o en otro
   * dispositivo (`resyncCampus`). Una sola política de caché para los dos casos.
   */
  async applyCampusChange(user: User) {
    // Por UserService, no sólo a storage: las queries acotadas por campus leen el
    // campus de `user$` para armar su llave de caché.
    await this.userService.updateUser(user);
    await this.#queryClient.cancelQueries();
    // Qué se vuelve a pedir y qué no se decide AQUÍ, por tipo de dato, y no en cada
    // pantalla: las páginas del campus anterior siguen montadas (Ionic conserva la
    // última de cada tab) y no hay forma fiable de que se apaguen solas a tiempo —
    // un `enabled` nuevo viaja al QueryObserver dentro de un effect que sólo corre
    // en el siguiente change detection, y al cambiar de tab Ionic ni siquiera emite
    // `ionViewWillLeave`. Por eso el detalle de una pantalla se marca stale y ya:
    // si el usuario no está ahí, no tiene por qué pedirse (y si vuelve, ese boleto
    // o esa ruta son de otro campus: el API respondería 404).
    await this.#queryClient.invalidateQueries({
      predicate: (query) => isCampusScopedDetailKey(query.queryKey),
      refetchType: 'none',
    });
    // El resto (catálogos y datos del usuario, que viven en stores de root y no en
    // una pantalla concreta) sí se recarga en el acto, como siempre.
    await this.#queryClient.invalidateQueries({
      predicate: (query) => !isCampusScopedDetailKey(query.queryKey),
    });
  }

  /**
   * El API respondió 409 `campus_changed` (ADR 0002): la petición se armó para un campus que el
   * usuario ya no tiene, porque lo cambió en otro dispositivo o se lo cambió soporte. Relee `me` y,
   * si de verdad cambió, aplica el cambio como si se hubiera hecho aquí y avisa. Si es el mismo campus
   * (una petición que salió justo antes de un `switchCampus` en este dispositivo), no hace nada.
   *
   * Una sola relectura aunque fallen varias peticiones a la vez: todas esperan la misma promesa.
   */
  resyncCampus(): Promise<void> {
    this.#campusResync ??= this.#resyncCampus().finally(() => (this.#campusResync = null));
    return this.#campusResync;
  }

  async #resyncCampus() {
    // Un 409 de una petición que salió mientras este dispositivo cambiaba de campus: primero que
    // termine ese cambio, o `before` sería el campus anterior y avisaría de un cambio "en otro lado".
    await this.#campusSwitch;
    const before = (await this.getUser())?.campus_current ?? null;
    const user: User | null = await this.getUserFromServer().catch(() => null);
    if (!user || user.campus_current === before) {
      return;
    }
    await this.applyCampusChange(user);
    await this.presentToast(`Tu campus cambió a ${user.campus_actual?.name ?? 'otro campus'}.`);
  }

  async login(form) {
    this.loadingService.present();
    return this.api
      .postData('login', form)
      .then((res) => {
        if (res && res.success) {
          // Token must be persisted before the user is emitted (setSession does both, in
          // that order): the emission enables user-gated queries (e.g. DriverStore), and
          // firing them without a token yields a 401 whose interceptor logs the session
          // right back out.
          return this.#session.setSession<User>(res.user, res.api_token).then(() => {
            if (res.realtrip) {
              this.storage.set('realtrip', res.realtrip);
            }
            Sentry.setUser({ id: String(res.user.id) }); // sin correo: PII (auditoría 2026-10)
            this.redirectTo(res.user);
            this.#queryClient.invalidateQueries();
          });
        } else {
          return res.errors;
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  loginDriver(form) {
    this.loadingService.present();
    return this.api
      .postData('loginpin', form)
      .then((res) => {
        if (res && res.success) {
          if (res.bus) {
            this.storage.set('bus', res.bus);
          }
          if (res.routes) {
            this.storage.set('routes', res.routes);
          }
          // Same ordering constraint as login(): token before the user emission.
          return this.#session.setSession<User>(res.user, res.api_token).then(() => {
            if (res.realtrip) {
              this.storage.set('realtrip', res.realtrip);
            }
            Sentry.setUser({ id: String(res.user.id) }); // sin correo: PII (auditoría 2026-10)
            this.redirectTo(res.user);
            this.#queryClient.invalidateQueries();
          });
        } else {
          return res.message;
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  register(form) {
    this.loadingService.present();
    return this.api
      .postData('' + form.endpoint, form)
      .then((res) => {
        if (res && res.success) {
          // Same ordering constraint as login(): token before the user emission.
          return this.#session.setSession<User>(res.user, res.api_token).then(() => {
            this.redirectTo(res.user);
            this.#queryClient.invalidateQueries();
          });
        } else {
          return res.errors;
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  update(form) {
    this.loadingService.present();
    return this.api
      .putData('user', form, { errorBody: true })
      .then(async (res) => {
        if (res && res.success) {
          // TEC-380: por updateUser, como el resto de las respuestas con usuario: las pantallas que leen
          // `user$` ven el perfil editado sin recargar. El API responde `me` completo (UserResource).
          await this.userService.updateUser(res.user);
          return res.user;
        } else {
          // { errors: string[] } (422 con motivo, o la respuesta genérica de handleError).
          return { errors: res?.errors ?? (res?.error ? [res.error] : []) };
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  /**
   * Cierra la sesión: `SessionService.logout()` con los hooks de Movilidad (`MovilidadSessionHooks`).
   * Mismo orden de siempre: Sentry, baja del push, user$ en null, queries y DriverStore, llaves de
   * almacenamiento, overlays y navegación a `/`. Un segundo llamado mientras corre no hace nada.
   */
  logout() {
    return this.#session.logout();
  }

  resendMail() {
    this.loadingService.present('Reenviando correo...');
    this.api
      .postData('resend/email', {})
      .then(
        (res) => {
          this.presentToast('Correo enviado exitosamente.');
        },
        (err) => {
          this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
        }
      )
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  forgotMail(form) {
    this.loadingService.present('Enviando correo...');
    this.api
      .postData('forgot', form)
      .then(
        (res) => {
          this.presentToast('Correo enviado exitosamente.');
        },
        (err) => {
          this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
        }
      )
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  isAuthenticated() {
    return this.#session.isAuthenticated();
  }

  async hasMealplan() {
    const user = (await this.#session.getStoredUser<User>()) || null;
    if (user && user.borrego === 1) {
      return true;
    } else {
      return false;
    }
  }

  async canExpresotec() {
    const user = (await this.#session.getStoredUser<User>()) || null;
    if (
      user &&
      typeof user.campus_registered !== 'undefined' &&
      user.campus_registered === 1 &&
      user.campus_current === 1 &&
      (user.type === 'Estudiante' || user.type === 'Representativo' || user.type === 'Colaborador')
    ) {
      return true;
    } else {
      return false;
    }
  }

  async getMembership() {
    return this.api
      .getData('membership')
      .then((res) => {
        if (res && res.subscription) {
          return true;
        } else {
          return false;
        }
      })
      .catch(() => {
        return false;
      });
  }

  async mayExpresotec() {
    const user = (await this.#session.getStoredUser<User>()) || null;
    if (
      user &&
      typeof user.campus_registered !== 'undefined' &&
      user.campus_registered === 1 &&
      !user.expresotec &&
      user.campus_current === 1 &&
      (user.type === 'Estudiante' || user.type === 'Representativo' || user.type === 'Colaborador')
    ) {
      return this.api
        .getData('membership')
        .then((res) => {
          if (res && res.school_period) {
            return true;
          } else {
            return false;
          }
        })
        .catch(() => {
          return false;
        });
    }
    return false;
  }

  async canTransportec() {
    const user = (await this.#session.getStoredUser<User>()) || null;
    if (
      user &&
      typeof user.campus_registered !== 'undefined' &&
      user.campus_registered > 2 &&
      user.campus_current > 2 &&
      (user.type === 'Estudiante' || user.type === 'Representativo' || user.type === 'Colaborador')
    ) {
      return true;
    } else {
      return false;
    }
  }

  async getBusiness() {
    const user = (await this.#session.getStoredUser<User>()) || null;
    if (user.type2 === 'business') {
      let business = await this.storage.get('business');
      if (!business) {
        business = user.business[0];
      }
      if (typeof business === 'string') {
        try {
          business = JSON.parse(business);
        } catch (e) {
          console.error(e);
        }
      }
      await this.storage.set('business', business);
      return business;
    } else {
      this.logout();
      return false;
    }
  }

  async askExit() {
    const alert = await this.alertCtrl.create({
      header: 'Cerrar TecService',
      message: '¿Esta seguro que quiere salir de la aplicación?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          handler: () => {
            console.log('Application exit prevented!');
          },
        },
        {
          text: 'Cerrar App',
          handler: () => {
            navigator['app'].exitApp();
          },
        },
      ],
    });
    alert.present();
  }

  async getSubscription_() {
    const subscription = (await this.storage.get('subscription')) || null;
    if (subscription) {
      return subscription;
    } else {
      return this.api
        .getData('suscription')
        .then((res) => {
          if (res) {
            this.storage.set('subscription', res.data);
            return res.data;
          }
        })
        .catch((err) => {
          this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
        })
        .finally(() => {});
    }
  }

  async getSubscription() {
    return this.api
      .getData('suscription')
      .then((res) => {
        if (!res || !res.success) {
          return;
        }
        this.storage.set('subscription', res.data);
        return res.data;
      })
      .catch((err) => {
        this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
      })
      .finally(() => {});
  }

  get(type) {
    return this.api
      .getData('' + type)
      .then((res) => {
        if (res) {
          return res;
        }
      })
      .catch((err) => {
        this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
      })
      .finally(() => {});
  }

  async singleLogOut() {
    try {
      const isSsoLogin = await this.storage.get('ssologin');
      if (!isSsoLogin) {
        this.logout();
        return;
      }
      this.loadingService.present();

      const sloRedirect = await this.api.postData('logout', {});
      this.loadingService.dismiss();
      const ssoUrl = sloRedirect.redirect_to;
      if (!ssoUrl) {
        this.logout();
        return;
      }
      const listener = await this.#browser.addUrlChangeListener(async (event) => {
        if (event.url.includes('saml2/success')) {
          this.logout();
          listener.remove();
          await this.#browser.close();
        }
      });
      await this.#browser.openInApp(ssoUrl, { title: 'Cerrando sesión...', showArrow: false });
    } catch (error) {
      this.loadingService.dismiss();
    }
  }

  async singleSignOn() {
    try {
      this.loadingService.present();

      const ssoRedirect = await this.api.postData('saml2/tec', {});
      this.loadingService.dismiss();
      const ssoUrl = ssoRedirect.redirect_to;
      const listener = await this.#browser.addUrlChangeListener(async (event) => {
        if (!event.url.includes('saml2/success')) {
          return;
        }
        const queryParams = this.getUrlParametersFromUrl(event.url);
        if (queryParams['action'] === 'register') {
          // The profile params only prefill the form; `ticket` is what the API registers.
          this.openRegisterSamlModal(queryParams, 'register', queryParams['ticket']);
          listener.remove();
          await this.#browser.close();
          return;
        }
        if (queryParams['token'] && queryParams['id']) {
          const token = queryParams['token'];
          const user = {
            id: +queryParams['id'],
            name: queryParams['name'],
            lastname: queryParams['lastname'],
            fullname: queryParams['fullname'],
            roster: queryParams['roster'],
            email: queryParams['email'],
            role: queryParams['role'],
            enrollment: queryParams['enrollment'],
            active: +queryParams['active'],
            campus_registered: +queryParams['campus_registered'],
            campus_current: +queryParams['campus_current'],
            type: queryParams['type'],
            type2: queryParams['type2'],
            status: queryParams['status'],
            campus_registered_name: queryParams['campus_registered_name'],
            user_type_id: +queryParams['user_type_id'],
          };
          // Token y luego usuario, como en login().
          await this.#session.setSession<User>(user, token);
          await this.storage.set('ssologin', true);
          Sentry.setUser({ id: String(user.id) }); // sin correo: PII (auditoría 2026-10)
          this.#queryClient.invalidateQueries();
          listener.remove();
          await this.#browser.close();
          this.navCtrl.navigateRoot(['/tecservice-transportec/wallet']);
        }
      });
      await this.#browser.openInApp(ssoUrl);
    } catch (error) {
      this.loadingService.dismiss();
    }
  }

  private async presentTermsAndConditions(queryParams) {
    // Auditoría 2026-10 (pentest XSS-01): IonicSafeString apaga el sanitizador de Ionic y el valor
    // viene de la URL de regreso del SSO; sólo un número entra al HTML.
    const campusId = Number.parseInt(String(queryParams['campus_registered'] ?? ''), 10) || '';
    const alert = await this.alertCtrl.create({
      header: 'Términos y condiciones',
      message: new IonicSafeString(
        `Para poder continuar, es necesario que aceptes los <a href="https://tecservice.app/terminosycondiciones?campus=${campusId}" target="_blank">términos y condiciones</a>.`
      ),
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          handler: () => {
            this.logout();
          },
        },
        {
          text: 'Aceptar',
          handler: () => {
            this.openRegisterSamlModal(queryParams, 'register', queryParams['ticket']);
          },
        },
      ],
    });
    alert.present();
  }

  private openTerms(campusId: number) {
    this.#browser.openInApp('https://tecservice.app/terminosycondiciones?campus=' + campusId);
  }

  private async openRegisterSamlModal(user: Partial<User>, mode = 'register', ticket: string | null = null) {
    const openedModal = await this.modalCtrl.getTop();
    if (openedModal) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: RegisterSamlModalComponent,
      componentProps: { user, mode, ticket },
    });

    modal.onDidDismiss().then((response) => {
      if (response?.data && response?.data?.api_token && response?.data?.user) {
        // Sin esperar ninguno de los dos, como siempre.
        void this.#session.setToken(response.data.api_token);
        this.userService.updateUser(response.data.user);
        this.#queryClient.invalidateQueries();
        this.navCtrl.navigateRoot(['/tecservice-transportec/wallet']);
      }
    });

    return await modal.present();
  }

  private getUrlParametersFromUrl(url: string): Record<string, string> {
    const queryString = url.split('?')[1];
    const urlParams = new URLSearchParams(queryString);
    const params: Record<string, string> = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  #checkTermsAndConditions(user: User) {
    // Abrir también cuando falte solo la privacidad: editar el aviso en admin genera una
    // versión nueva y el backend devuelve privacy=false con terms=true. La modal cubre ambas
    // pestañas y al aceptar reenvía terms+privacy (recordAcceptance es idempotente), así que
    // se resuelven los dos. Antes solo miraba `terms`, ignorando ese caso.
    if (user && (!user.terms || !user.privacy)) {
      this.openRegisterSamlModal(user, 'update');
    }
  }
}
